import { useState, useRef, useCallback, useEffect } from 'react';
import { GameState, Player, VoiceParsedEvent, VoiceParseResult } from '../types';
import { parseVoiceTranscript, VoiceGameContext } from '../services/geminiService';

// Voice logging records a short audio clip with MediaRecorder and sends it to
// /api/gemini/voice-parse, where Gemini transcribes it WITH the roster in
// context and parses it into events in one call. (Previously the browser's Web
// Speech API produced the transcript, which mangled player names and jargon and
// behaved differently per browser.)

// SimplePlayer shape used in opponentRoster
interface SimplePlayer { id: string; name: string; number: string; position: string }

export type VoiceState = 'idle' | 'recording' | 'processing' | 'pending';
export type QuotaWarning = 'moderate' | 'high' | 'critical' | null;

interface UseVoiceLoggerOptions {
  gameState: GameState;
  teamPlayers: Player[];
  opponentRoster: (Player | SimplePlayer)[];
  organizationId: string;
}

// Hard stop so a forgotten recording can't run up a huge upload.
export const MAX_RECORDING_SECONDS = 45;

// Preferred container/codec by browser. Chrome/Android/Firefox → webm or ogg opus;
// Safari/iOS → mp4 (AAC). The server maps these onto Gemini's accepted types.
const CANDIDATE_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/ogg',
  'audio/mp4',
];

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') return undefined;
  return CANDIDATE_MIME_TYPES.find(t => MediaRecorder.isTypeSupported(t));
}

function isRecordingSupported(): boolean {
  return typeof window !== 'undefined'
    && typeof MediaRecorder !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Strip the "data:<mime>;base64," prefix
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

export function useVoiceLogger({
  gameState,
  teamPlayers,
  opponentRoster,
  organizationId,
}: UseVoiceLoggerOptions) {
  const [voiceState, setVoiceState] = useState<VoiceState>('idle');
  const [pendingEvents, setPendingEvents] = useState<VoiceParsedEvent[]>([]);
  // Transcript as heard by the model; populated after parsing (not live).
  const [liveTranscript, setLiveTranscript] = useState('');
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [quotaWarning, setQuotaWarning] = useState<QuotaWarning>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const cancelledRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const startedAtRef = useRef(0);

  // Keep latest values accessible inside recorder callbacks without stale closures
  const gameStateRef = useRef(gameState);
  gameStateRef.current = gameState;
  const teamPlayersRef = useRef(teamPlayers);
  teamPlayersRef.current = teamPlayers;
  const opponentRosterRef = useRef(opponentRoster);
  opponentRosterRef.current = opponentRoster;
  const orgIdRef = useRef(organizationId);
  orgIdRef.current = organizationId;

  const isSupported = isRecordingSupported();

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Release the mic if the component unmounts mid-recording
  useEffect(() => releaseStream, [releaseStream]);

  const buildGameContext = useCallback((): VoiceGameContext => {
    const gs = gameStateRef.current;
    const tp = teamPlayersRef.current;
    const or = opponentRosterRef.current;

    const ourLineup: VoiceGameContext['ourLineup'] = {};
    for (const [slot, playerId] of Object.entries(gs.lineup)) {
      const p = tp.find(pl => pl.id === playerId);
      ourLineup[slot] = p ? { id: p.id, name: p.name, jerseyNumber: p.jerseyNumber, position: p.position } : null;
    }

    const ourRoster = tp.map(p => ({ id: p.id, name: p.name, jerseyNumber: p.jerseyNumber, position: p.position }));

    const hasOpponent = or.length > 0;
    let opponentLineup: VoiceGameContext['opponentLineup'] = null;
    let opponentRosterOut: VoiceGameContext['opponentRoster'] = null;

    if (hasOpponent) {
      opponentLineup = {};
      for (const [slot, playerId] of Object.entries(gs.opponentLineup)) {
        const p = or.find(pl => pl.id === playerId);
        if (p) {
          const jn = 'jerseyNumber' in p ? (p as Player).jerseyNumber : (p as SimplePlayer).number;
          opponentLineup[slot] = { id: p.id, name: p.name, jerseyNumber: jn };
        } else {
          opponentLineup[slot] = null;
        }
      }
      opponentRosterOut = or.map(p => {
        const jn = 'jerseyNumber' in p ? (p as Player).jerseyNumber : (p as SimplePlayer).number;
        return { id: p.id, name: p.name, jerseyNumber: jn };
      });
    }

    return { ourLineup, ourRoster, opponentLineup, opponentRoster: opponentRosterOut };
  }, []);

  const applyResult = useCallback((result: VoiceParseResult) => {
    setQuotaWarning(result.quotaStatus.warning);
    setLiveTranscript(result.transcript ?? '');
    if (result.events.length === 0) {
      setError(result.transcript ? `No stats detected in "${result.transcript}" — try again` : 'No stats detected — try again');
      setVoiceState('idle');
      return;
    }
    // If a kill (our team) was logged without an assist, suggest one for the active setter
    const events = [...result.events];
    const hasOurKill = events.some(e => e.eventType === 'stat' && e.statType === 'kill' && e.team === 'us' && e.resolvedPlayerId);
    const hasAssist  = events.some(e => e.eventType === 'stat' && e.statType === 'assist' && e.team === 'us');
    if (hasOurKill && !hasAssist) {
      const gs = gameStateRef.current;
      const tp = teamPlayersRef.current;
      const setter = tp.find(p => p.position === 'Setter' && Object.values(gs.lineup).includes(p.id));
      if (setter) {
        events.push({
          eventType: 'stat',
          statType: 'assist',
          resolvedPlayerId: setter.id,
          resolvedPlayerName: setter.name,
          playerRef: setter.name,
          team: 'us',
          confidence: 1,
          suggested: true,
        });
      }
    }
    setPendingEvents(events);
    setVoiceState('pending');
  }, []);

  const processAudio = useCallback(async (blob: Blob, mimeType: string) => {
    try {
      const data = await blobToBase64(blob);
      const result = await parseVoiceTranscript({
        audio: { data, mimeType },
        gameContext: buildGameContext(),
        orgId: orgIdRef.current,
      });
      applyResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Parsing failed');
      setVoiceState('idle');
    }
  }, [buildGameContext, applyResult]);

  /** Text fallback (e.g. typed input or tests) — same parse pipeline without audio. */
  const processTranscript = useCallback(async (transcript: string) => {
    setVoiceState('processing');
    try {
      const result = await parseVoiceTranscript({
        transcript,
        gameContext: buildGameContext(),
        orgId: orgIdRef.current,
      });
      applyResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Parsing failed');
      setVoiceState('idle');
    }
  }, [buildGameContext, applyResult]);

  const startRecording = useCallback(async () => {
    if (!isRecordingSupported()) {
      setError('Voice logging is not supported in this browser.');
      return;
    }
    if (recorderRef.current) return; // already recording

    setError(null);
    setLiveTranscript('');
    setRecordingSeconds(0);
    chunksRef.current = [];
    cancelledRef.current = false;

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (err) {
      const name = (err as DOMException)?.name;
      setError(name === 'NotAllowedError'
        ? 'Microphone access was blocked. Allow the microphone for this site and try again.'
        : `Microphone error: ${name || 'unavailable'}`);
      return;
    }

    const mimeType = pickMimeType();
    let recorder: MediaRecorder;
    try {
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    } catch {
      stream.getTracks().forEach(t => t.stop());
      setError('Could not start the recorder in this browser.');
      return;
    }

    recorder.ondataavailable = (e: BlobEvent) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onerror = () => {
      releaseStream();
      setError('Recording failed — try again');
      setVoiceState('idle');
    };

    recorder.onstop = () => {
      const type = recorder.mimeType || mimeType || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type });
      chunksRef.current = [];
      releaseStream();

      if (cancelledRef.current) {
        setVoiceState('idle');
        return;
      }
      // Anything under ~0.4s is a stray tap, not speech
      if (blob.size < 2000) {
        setVoiceState('idle');
        return;
      }
      setVoiceState('processing');
      processAudio(blob, type);
    };

    streamRef.current = stream;
    recorderRef.current = recorder;
    startedAtRef.current = Date.now();
    recorder.start(250); // gather chunks every 250ms so a crash mid-clip still yields audio

    timerRef.current = window.setInterval(() => {
      const secs = Math.floor((Date.now() - startedAtRef.current) / 1000);
      setRecordingSeconds(secs);
      if (secs >= MAX_RECORDING_SECONDS && recorderRef.current?.state === 'recording') {
        recorderRef.current.stop();
      }
    }, 250);

    if ('vibrate' in navigator) navigator.vibrate(60);
    setVoiceState('recording');
  }, [processAudio, releaseStream]);

  /** Stop and send the clip for parsing. */
  const stopRecording = useCallback(() => {
    const r = recorderRef.current;
    if (!r) return;
    if ('vibrate' in navigator) navigator.vibrate([40, 40, 40]);
    if (r.state !== 'inactive') r.stop(); // onstop → processAudio
  }, []);

  /** Stop and discard the clip without parsing. */
  const cancelRecording = useCallback(() => {
    cancelledRef.current = true;
    const r = recorderRef.current;
    if (r && r.state !== 'inactive') {
      r.stop(); // onstop sees cancelledRef and returns to idle
    } else {
      releaseStream();
      setVoiceState('idle');
    }
    setLiveTranscript('');
    setError(null);
  }, [releaseStream]);

  const clearPending = useCallback(() => {
    setPendingEvents([]);
    setVoiceState('idle');
    setLiveTranscript('');
    setError(null);
  }, []);

  return {
    voiceState,
    pendingEvents,
    setPendingEvents,
    liveTranscript,
    recordingSeconds,
    error,
    quotaWarning,
    isSupported,
    startRecording,
    stopRecording,
    cancelRecording,
    processTranscript,
    clearPending,
  };
}
